import { DestroyRef, Directive, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, map, Observable, shareReplay, Subscription, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { SearchState, SortingOrderType } from 'src/app/interfaces/search.interface';
import { PendingUserInvolvementInSocialGroup, SocialGroupService } from 'src/app/interfaces/social-groups.service';
import { TableCellOnActionEvent, UserTableConstructorInfo } from 'src/app/interfaces/tables.interfaces';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { SearchResponse } from 'src/app/swagger-api';
import { ColumnTypes } from 'src/app/views/grid-user-list/grid-table.enum';
import {
  DEFAULT_SORT_OPTIONS,
  NAME_SORT_OPTIONS,
  SortCategories,
  SortOptions,
} from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';
import { SearchCategories, SearchStrategyTypes } from '../../search/search.enum';
import { SearchService } from '../../search/search.service';
import { CellActionTypes } from '../../tables/components/cell-actions.type';
import { filterDataForEntityTable } from '../../tables/table.helpers';
import {
  actionButtonTestId,
  buildSorting,
} from '../../user-relation-requests/components/manage-relation-request/manage-relation-request.helper';
import { RequestCounters } from '../../user-relation-requests/relation-request.interface';
import {
  INITIAL_PAGINATION,
  ListController,
  ListMode,
  ListState,
  paginationParams,
  sortParams,
} from '../list-controller/list-controller';
import { PaginationState } from '../pagination/pagination.interface';

type SocialGroupTabs = 'all_social_groups' | 'sent_requests' | 'got_invitations';

export interface Counters {
  verifiedCounter: number;
  sentCounter: number;
  receivedCounter: number;
}

@Directive()
export abstract class SocialGroupInvolvementContext<TEntity> implements OnInit {
  private destroyRef = inject(DestroyRef);
  private searchService = inject(SearchService);
  protected sessionService = inject(SessionService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  private socialGroupSearch$: BehaviorSubject<{ items: TEntity[]; totalCount: number }> = new BehaviorSubject({
    totalCount: 0,
    items: [],
  });

  // Success messages
  // - Confirm invite
  protected abstract MANAGE_SOCIAL_GROUP_CONFIRM_INVITE_HINT: string;
  // - Reject invite
  protected abstract MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_DESCRIPTION: string;
  protected abstract MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_TITLE: string;
  protected abstract MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_BTN_HINT: string;
  // - Delete invite
  protected abstract MANAGE_SOCIAL_GROUP_REJECT_USER_REQUEST_HINT: string;

  protected abstract SEARCH_INPUT_LABEL: string;

  // Sorting
  public readonly SORT_OPTIONS: SortOptions[] = DEFAULT_SORT_OPTIONS;
  public readonly NAME_SORT_OPTIONS: SortOptions[] = NAME_SORT_OPTIONS;

  public ListMode: typeof ListMode = ListMode;

  private readonly activeTab = new ListController<SocialGroupTabs, string>({
    filtering: '',
    queryKeyword: '',
    context: 'all_social_groups',
    mode: ListMode.BROWSE,
    pagination: INITIAL_PAGINATION,
    sorting: SortCategories.DATE_DESC,
  });

  protected readonly activeTab$ = this.activeTab.state$.pipe(shareReplay(1));

  protected readonly sortParams$ = this.activeTab.state$.pipe(
    map((activeTab: ListState<SocialGroupTabs, string>) => sortParams[activeTab.sorting]),
  );

  public readonly isLoading$: Observable<boolean> = this.operationInfoService.isLoadingLocallSpinner$;

  public get counters$(): Observable<Counters> {
    return this.socialGroupService.counterChanges.pipe(
      shareReplay(1),
      map(({ verified_count, requests_received, requests_sent }: RequestCounters) => ({
        verifiedCounter: verified_count,
        sentCounter: requests_sent,
        receivedCounter: requests_received,
      })),
    );
  }

  public get requestsTableData$(): Observable<UserTableConstructorInfo> {
    return this.socialGroupService.pendingRequestsChanges.pipe(
      tap(({ total_count }) => {
        this.activeTab.updateCount(total_count);
      }),
      map(({ items }) => this.createRequestsTableData(items, this.sessionService.userId)),
    );
  }

  public get invitesTableData$(): Observable<UserTableConstructorInfo> {
    return this.socialGroupService.pendingInvitesChanges.pipe(
      tap(({ total_count }) => {
        this.activeTab.updateCount(total_count);
      }),
      map(({ items }) => this.createInvitesTableData(items, this.sessionService.userId)),
    );
  }

  public get userSocialGropus$(): Observable<TEntity[]> {
    return this.socialGroupService.socialGroupChanges.pipe(
      tap((groups) => {
        this.activeTab.updateCount(groups.total_count);
      }),
      map(({ items }) => items),
    );
  }

  public get searchSocialGroup$(): Observable<TEntity[]> {
    return this.socialGroupSearch$.pipe(
      tap(({ totalCount }) => {
        this.activeTab.updateCount(totalCount);
      }),
      map(({ items }) => items),
    );
  }

  protected subscriptions$: Subscription[] = [];

  public ngOnInit(): void {
    this.socialGroupService.fetchRequestCounters();

    this.activeTab.params$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params: ListState<SocialGroupTabs, string>) => {
          const { context, pagination, sorting, mode, queryKeyword } = params;

          mode === ListMode.BROWSE
            ? this.loadUserSocialGroups(context, pagination, sorting)
            : this.loadSearchSocialGroups(queryKeyword, pagination, sorting);
        }),
      )
      .subscribe();

    this.searchService.searchResultsChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ total_matches_count, search_results }: SearchResponse) => ({
          totalCount: total_matches_count,
          items: search_results.map(({ item }) => item as TEntity),
        })),
        tap((result) => {
          this.socialGroupSearch$.next(result);
        }),
      )
      .subscribe();
  }

  public onTabClick($event: NgbNavChangeEvent<SocialGroupTabs>): void {
    this.socialGroupService.fetchRequestCounters();

    this.activeTab.update({
      filtering: '',
      queryKeyword: '',
      context: $event.nextId,
      mode: ListMode.BROWSE,
      pagination: INITIAL_PAGINATION,
      sorting: SortCategories.DATE_DESC,
    });
  }

  public onSortParamChanged(orderType: SortingOrderType): void {
    const { snapshot } = this.activeTab;

    this.activeTab.patch({ sorting: buildSorting(snapshot.sorting, orderType) });
  }

  public onKeywordChanged(keyword: string): void {
    const { snapshot } = this.activeTab;
    const { pagination, filtering, queryKeyword } = this.activeTab.initialState;

    const state: ListState<SocialGroupTabs, string> = {
      ...snapshot,
      pagination,
      filtering,
      queryKeyword,
      sorting: SortCategories.SCORE_DESC,
      mode: ListMode.SEARCH,
    };

    const _keyword: string = keyword.trim();

    if (snapshot.mode === ListMode.BROWSE && _keyword) {
      this.activeTab.patch({ ...state, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && _keyword) {
      this.activeTab.patch({ ...snapshot, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && !_keyword) {
      this.activeTab.patch({ ...state, mode: ListMode.BROWSE, queryKeyword: '' });
      return;
    }
  }

  public onPageChanged(currentPage: number): void {
    const { pagination } = this.activeTab;

    this.activeTab.patch({ pagination: { ...pagination, currentPage } });
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.activeTab.patch({ sorting: sortOptions.sortCategory });
  }

  public applyDefaultParams(): void {
    const { pagination, sorting, filtering, queryKeyword, mode } = this.activeTab.initialState;

    this.activeTab.patch({
      mode,
      pagination,
      sorting,
      filtering,
      queryKeyword,
    });
  }

  // Data controllers
  public onTableAction(event: TableCellOnActionEvent): void {
    const actions: { [key: string]: () => void } = {
      [CellActionTypes.CONFIRM]: () => this.onConfirmRequestOrInvite(event),
      [CellActionTypes.REJECT]: () => this.onRejectRequestOrInvite(event),
      [CellActionTypes.REMOVE]: () => this.onRemoveInvite(event),
    };

    actions[event.type] && actions[event.type]();
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        successfulMessage: this.MANAGE_SOCIAL_GROUP_CONFIRM_INVITE_HINT,
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.REJECT, event) },
        ],
      },
      () => {
        this.subscriptions$.push(
          this.socialGroupService.confirmRequestOrInvite(event).subscribe(() => {
            this.onListUpdate();
          }),
        );
      },
    );
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        ],
        successfulMessage: this.MANAGE_SOCIAL_GROUP_REJECT_USER_REQUEST_HINT,
        confirmationDialog: {
          description: this.MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_DESCRIPTION,
          title: this.MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_TITLE,
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              this.MANAGE_SOCIAL_GROUP_REJECT_REQUEST_DIALOG_BTN_HINT,
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.subscriptions$.push(
          this.socialGroupService.rejectRequestOrInvite(event).subscribe(() => {
            this.onListUpdate();
          }),
        );
      },
    );
  }

  public onRemoveInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'MANAGE_ENTITY_REMOVE_USER_REQUEST_HINT',
        confirmationDialog: {
          description: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_DESCRIPTION',
          title: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.subscriptions$.push(
          this.socialGroupService.removeInvite(event).subscribe(() => {
            this.onListUpdate();
          }),
        );
      },
    );
  }

  protected onListUpdate(): void {
    const { pagination } = this.activeTab;

    const isPageWithLastItem: boolean = // total - offset = 1
      pagination.totalItemsCount - pagination.limit * (pagination.currentPage - 1) === 1;

    if (pagination.currentPage !== 1 && isPageWithLastItem) {
      this.activeTab.updatePage(pagination.currentPage - 1);
    }

    this.activeTab.triggerParams();
    this.socialGroupService.fetchRequestCounters();
  }

  private loadSearchSocialGroups(keyword: string, pagination: PaginationState, sorting: SortCategories): void {
    const state: SearchState = {
      pagination,
      sortParams: sortParams[sorting],
      params: { queryText: keyword, itemKind: this.searchCategories() },
      excludeIds: [this.sessionService.userId],
    };

    if (!keyword) {
      return;
    }

    this.searchService.setStrategy(SearchStrategyTypes.DEFAULT);
    this.searchService.getSearchResults({ state, applyLocationAttrs: true });

    //
  }

  private loadUserSocialGroups(tabId: SocialGroupTabs, pagination: PaginationState, sorting: SortCategories): void {
    const [offset, limit] = paginationParams(pagination);

    type DataLoaders = Record<SocialGroupTabs, () => void>;

    const loader: DataLoaders = {
      all_social_groups: () => this.socialGroupService.fetchPersonalEntities({ pagination }),
      got_invitations: () => this.socialGroupService.fetchUserPersonalInvites(offset, limit, sorting),
      sent_requests: () => this.socialGroupService.fetchUserPersonalRequests(offset, limit, sorting),
    };

    loader[tabId]();
  }

  protected createRequestsTableData(
    items: PendingUserInvolvementInSocialGroup[],
    userId: string,
  ): UserTableConstructorInfo {
    return filterDataForEntityTable(
      TableCreatorFactory.createTableConstructorData(this.userRequestTableCreators(), {
        personalEntities: items,
        actorId: userId,
      }),
      this.columnTypes(),
    );
  }

  protected createInvitesTableData(
    items: PendingUserInvolvementInSocialGroup[],
    userId: string,
  ): UserTableConstructorInfo {
    return filterDataForEntityTable(
      TableCreatorFactory.createTableConstructorData(this.userInvitesTableCreators(), {
        personalEntities: items,
        actorId: userId,
      }),
      this.columnTypes(),
    );
  }

  protected recallCountersRequest(): void {
    this.subscriptions$.push(
      this.socialGroupService.requestCounter.subscribe(() => {
        this.socialGroupService.fetchRequestCounters();
      }),
    );
  }

  public get socialGroupService(): SocialGroupService<TEntity> {
    return this.getSocialGroupService();
  }

  // Abstract controllers

  protected abstract getSocialGroupService(): SocialGroupService<TEntity>;

  protected abstract columnTypes(): ColumnTypes;

  protected abstract userRequestTableCreators(): UserTableCreators;

  protected abstract userInvitesTableCreators(): UserTableCreators;

  protected abstract searchCategories(): SearchCategories.COMMUNITIES | SearchCategories.PROJECTS;
}
